import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@material-ui/core';
import { Search as PageviewIcon, Delete as DeleteIcon } from '@material-ui/icons';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import TransportadoraContext from '../../../../../contexts/registrations/transportadora/TransportadoraContext';
import QCXSimpleConsultPageTemplate from '../../../../../templates/simple-consult-page/QCXSimpleConsultPageTemplate';
import {
  activateByIdAsync,
  changeToConsultMode,
  changeToCreateMode,
  fetchAllAsync,
  inactivateByIdAsync,
  resetBackgroundMode,
  resetMode,
  resetModel,
  selectTransportadoras,
} from '../../../../../features/transportadora/transportadoraSlice';
import { estadoActions } from '../../../../../features/estado/estadoSlice';
import useOperationController from '../../../../../utils/hooks/store/operation/useOperationController';
import { isAlternativeLoadingStatus } from '../../../../../utils/store/store-utils';
import { formatCnpj } from '../../../../../utils/hooks/form/field/formatters';
import { userHasDeleteRole } from '../../../../../components/perfil/perfil-utils';
import { IDLE_CONTROL } from '../../../../../features/config-control';

export default function TransportadoraConsultPage({ authInfo = {} }) {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const { status } = useContext(TransportadoraContext);

  const transportadoras = useSelector(selectTransportadoras);

  const isAlternativeLoading = useMemo(() => isAlternativeLoadingStatus(status), [status]);

  const fetchAll = useCallback(() => {
    dispatch(fetchAllAsync());
  }, []);

  const resetModes = useCallback(() => {
    dispatch(resetMode());
    dispatch(resetBackgroundMode());
  }, []);

  const resetModels = useCallback(() => {
    dispatch(resetModel());
  }, []);

  const configureDependencies = useCallback(() => {
    dispatch(estadoActions.changeControlTo(IDLE_CONTROL));
  }, []);

  useEffect(() => {
    fetchAll();
    resetModes();
    resetModels();
  }, []);

  const handleActivateInactivate = useOperationController(async (event, id) => {
    event.preventDefault();

    const { active } = transportadoras.find((transportadora) => transportadora.id === id);

    const isActivation = !active;

    if (isActivation) {
      dispatch(activateByIdAsync(id));
    } else {
      dispatch(inactivateByIdAsync(id));
    }
  });

  const handleClickOnButtonNew = useCallback(
    (event) => {
      event.stopPropagation();
      configureDependencies();
      dispatch(changeToCreateMode());
    },
    [configureDependencies]
  );

  const handleClickOnEditButton = useCallback(
    (event, id) => {
      event.stopPropagation();
      configureDependencies();
      dispatch(changeToConsultMode());
      history.push(t('com.muralis.qcx.url.moduloOperacionaisTransportadorasDetalhes', { id }));
    },
    [history, configureDependencies]
  );

  const columns = [
    {
      field: 'id',
      headerName: t('com.muralis.qcx.codigo'),
      headerAlign: 'center',
      align: 'center',
      flex: 90,
      valueGetter: ({ row }) => row?.id,
    },
    {
      field: 'pessoaJuridica.nome',
      headerName: t('com.muralis.qcx.nome'),
      type: 'string',
      flex: 220,
      valueGetter: ({ row }) => row?.pessoaJuridica?.nome,
    },
    {
      field: 'pessoaJuridica.apelido',
      headerName: t('com.muralis.qcx.apelido'),
      type: 'string',
      flex: 220,
      valueGetter: ({ row }) => row?.pessoaJuridica?.nomeResumido,
    },
    {
      field: 'pessoaJuridica.cnpj',
      headerName: t('com.muralis.qcx.empresa.CNPJ'),
      headerAlign: 'center',
      align: 'center',
      flex: 145,
      valueGetter: ({ row }) => formatCnpj(row?.pessoaJuridica?.cnpj),
    },
    {
      field: 'importador',
      headerName: t('com.muralis.qcx.importador.label'),
      headerAlign: 'center',
      align: 'center',
      type: 'boolean',
      flex: 120,
      valueGetter: ({ row }) => row?.importador,
    },
    {
      field: 'exportador',
      headerName: t('com.muralis.qcx.exportador.label'),
      headerAlign: 'center',
      align: 'center',
      type: 'boolean',
      flex: 120,
      valueGetter: ({ row }) => row?.exportador,
    },
    {
      field: 'actions',
      headerName: t('com.muralis.qcx.acoes.label'),
      headerAlign: 'center',
      align: 'center',
      flex: 130,
      renderCell: ({ row: { id, active } }) => (
        <>
          {userHasDeleteRole(authInfo, 'transportadora') && (
            <IconButton
              key={`stw-active${id}`}
              value={active}
              name={`stw-active-${id}`}
              onClick={(event) => handleActivateInactivate(event, id)}
            >
              <DeleteIcon color="error" />
            </IconButton>
          )}
          <IconButton
            key={`btn-view-${id}`}
            name={`btn-view-${id}`}
            onClick={(event) => handleClickOnEditButton(event, id)}
          >
            <PageviewIcon color="primary" />
          </IconButton>
        </>
      ),
    },
  ];

  const breadcrumbs = [
    {
      link: {
        to: '/',
        name: t('com.muralis.qcx.inicio'),
      },
    },
    {
      text: {
        name: t('com.muralis.qcx.cadastros'),
      },
    },
    {
      link: {
        to: t('com.muralis.qcx.url.moduloOperacionais'),
        name: t('com.muralis.qcx.operacionais'),
      },
    },
    {
      text: {
        name: t('com.muralis.qcx.transportadora.labelPlural'),
      },
    },
  ];

  const templateProps = {
    page: {
      title: t('com.muralis.qcx.transportadora.labelPlural'),
      breadcrumbs,
    },
    control: {
      buttonNew: {
        description: t('com.muralis.qcx.transportadora.novaTransportadora'),
        path: t('com.muralis.qcx.url.moduloOperacionaisTransportadorasNova'),
        onClick: handleClickOnButtonNew,
      },
    },
    table: {
      columns,
    },
  };

  return (
    <QCXSimpleConsultPageTemplate
      pageProps={templateProps.page}
      controlProps={templateProps.control}
      tableProps={templateProps.table}
      tableData={transportadoras}
      isLoading={isAlternativeLoading}
      authInfo={authInfo}
      requiredRoles={['transportadora']}
    />
  );
}
